import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { MediaResponse } from '../types/Media';
import { getMediaLibrary, getAuthenticatedImageUrl } from '../services/mediaList';

interface MediaLibraryContextType {
  mediaLibraryData: MediaResponse;
  loading: boolean;
  error: string | null;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  activeTab: 'base-images' | 'ai-images' | 'other-images';
  refreshMediaLibrary: () => Promise<void>;
  getAuthenticatedImage: (imageUrl: string) => Promise<string>;
  goToPage: (page: number) => void;
  setActiveTab: (tab: 'base-images' | 'ai-images' | 'other-images') => void;
}

const MediaLibraryContext = createContext<MediaLibraryContextType | undefined>(undefined);

interface MediaLibraryProviderProps {
  children: ReactNode;
}

export const MediaLibraryProvider: React.FC<MediaLibraryProviderProps> = ({ children }) => {
  const [mediaLibraryData, setMediaLibraryData] = useState<MediaResponse>({
    bucket_name: '',
    fpath: '',
    limit: 0,
    offset: 0,
    images: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [activeTab, setActiveTabState] = useState<'base-images' | 'ai-images' | 'other-images'>('base-images');

  const totalPages = Math.ceil(totalItems / pageSize);

  const fetchMediaLibrary = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const offset = (currentPage - 1) * pageSize;
      
      const response = await getMediaLibrary('display-images', activeTab, pageSize, offset, 'last_modified', 'desc');

      const total = response.total || 0;

      setMediaLibraryData({
        bucket_name: response.bucket_name,
        fpath: response.fpath,
        limit: response.limit,
        offset: offset,
        images: response.images,
        total: total
      });
      setTotalItems(total);
      
    } catch (err) {
      setError('Failed to fetch media library');
      console.error('Error fetching media library:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshMediaLibrary = async () => {
    await fetchMediaLibrary();
  };

  const getAuthenticatedImage = useCallback(async (imageUrl: string): Promise<string> => {
    try {
      return await getAuthenticatedImageUrl(imageUrl);
    } catch (error) {
      console.error('Error getting authenticated image:', error);
      throw error;
    }
  }, []);

  useEffect(() => {
    fetchMediaLibrary();
  }, [currentPage, pageSize, activeTab]);

  const goToPage = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const setActiveTab = (tab: 'base-images' | 'ai-images' | 'other-images') => {
    setActiveTabState(tab);
    setCurrentPage(1);
  };

  const value: MediaLibraryContextType = {
    mediaLibraryData,
    loading,
    error,
    currentPage,
    pageSize,
    totalPages,
    activeTab,
    refreshMediaLibrary,
    getAuthenticatedImage,
    goToPage,
    setActiveTab,
  };

  return (
    <MediaLibraryContext.Provider value={value}>
      {children}
    </MediaLibraryContext.Provider>
  );
};

export const useMediaLibrary = (): MediaLibraryContextType => {
  const context = useContext(MediaLibraryContext);
  if (context === undefined) {
    throw new Error('useMediaLibrary must be used within a MediaLibraryProvider');
  }
  return context;
}; 